import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ServiceAgreementPanel from '../components/ServiceAgreementPanel';
import { authApi, checkoutApi, procurementApi } from '../services/api';
import {
  CLIENT_KEY_STORAGE,
  REGISTRATION_PATHWAY,
  REGISTRATION_PATHWAY_STORAGE,
  SCREENS,
} from '../lib/screenRegistry';
import { INSTITUTIONAL_EMAIL_HINT, isInstitutionalEmail } from '../lib/institutionalEmail';
import { getMasterTestingKey } from '../lib/masterTestingKey';
import { fireGtagEvent } from '../lib/gtag';
import './RegisterOrg.css';
import '../styles/registrationSplit.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');

type SeatTier = 10 | 50 | 100 | 'enterprise';
type OrgCheckoutPathway = 'card' | 'procurement';

const PLAN_SELECTION_REQUIRED_MESSAGE = 'Please select a pricing plan to continue to secure checkout.';

const SEAT_PLANS: {
  seats: SeatTier;
  planKey: string;
  title: string;
  price: string;
  limit: string;
  value: string;
  checkoutEnabled: boolean;
}[] = [
  {
    seats: 10,
    planKey: 'elt07-pilot',
    title: 'The Pilot Pack',
    price: '$200 / month',
    limit: 'Up to 10 Users',
    value: '($20 per user value)',
    checkoutEnabled: true,
  },
  {
    seats: 50,
    planKey: 'elt07-mid-tier',
    title: 'The Mid-Tier Pack',
    price: '$750 / month',
    limit: 'Up to 50 Users',
    value: '($15 per user value)',
    checkoutEnabled: true,
  },
  {
    seats: 100,
    planKey: 'elt07-full-pack',
    title: 'The Full Pack',
    price: '$1,000 / month',
    limit: 'Up to 100 Users',
    value: '($10 per user value)',
    checkoutEnabled: true,
  },
  {
    seats: 'enterprise',
    planKey: 'elt07-enterprise',
    title: 'Enterprise',
    price: 'Negotiated',
    limit: '100+ Users',
    value: '(Volume-based pricing)',
    checkoutEnabled: false,
  },
];

export default function RegisterOrg() {
  const navigate = useNavigate();
  const { masterSandboxUnlocked, refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const screenId = SCREENS.ELT07;
  const [form, setForm] = useState({
    fullName: '',
    orgEmail: '',
    orgName: '',
    businessAddress: '',
    phone: '',
    poNumber: '',
    accountsPayableEmail: '',
  });
  const [orgCheckoutPathway, setOrgCheckoutPathway] = useState<OrgCheckoutPathway>('card');
  const [institutionalAgreed, setInstitutionalAgreed] = useState(false);
  const [apBillingAddressAgreed, setApBillingAddressAgreed] = useState(false);
  const [procurementDemoIssued, setProcurementDemoIssued] = useState(false);
  const [selectedOrgPlanSeats, setSelectedOrgPlanSeats] = useState<SeatTier | null>(null);
  const [cardholderNameSameAsContact, setCardholderNameSameAsContact] = useState(true);
  const [billingSameAsBusiness, setBillingSameAsBusiness] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [copyNotice, setCopyNotice] = useState('');
  const [issuedInvoiceNumber, setIssuedInvoiceNumber] = useState('');
  const [invoiceDispatchNotice, setInvoiceDispatchNotice] = useState('');
  const [invoiceDownloadError, setInvoiceDownloadError] = useState('');

  useEffect(() => {
    if (!accessKey) return;
    fireGtagEvent('ai_mentor_enrolled', accessKey);
  }, [accessKey]);

  useEffect(() => {
    if (searchParams.get('screen') === 'pro01') {
      navigate('/register-private', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    if (checkoutStatus !== 'success' || !sessionId) return undefined;

    let cancelled = false;
    const poll = async () => {
      for (let attempt = 0; attempt < 12 && !cancelled; attempt += 1) {
        try {
          const result = await checkoutApi.getFulfillment(sessionId);
          if (result.subscriptionCode) {
            setAccessKey(result.subscriptionCode);
            return;
          }
        } catch {
          /* webhook may still be processing */
        }
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      if (!cancelled) {
        setError('Payment received — your subscription key is being issued. Refresh shortly or check your email.');
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [searchParams]);

  useEffect(() => {
    if (window.self === window.top) return undefined;

    document.documentElement.classList.add('maintenance-embed-preview');
    document.body.classList.add('maintenance-embed-preview');
    document.getElementById('root')?.classList.add('maintenance-embed-preview');

    return () => {
      document.documentElement.classList.remove('maintenance-embed-preview');
      document.body.classList.remove('maintenance-embed-preview');
      document.getElementById('root')?.classList.remove('maintenance-embed-preview');
    };
  }, []);

  const resolvedNameOnCard = cardholderNameSameAsContact
    ? form.fullName.trim()
    : '';

  const resolvedBillingAddress = billingSameAsBusiness
    ? form.businessAddress.trim()
    : '';

  const billingAddressValid = billingSameAsBusiness
    ? Boolean(form.businessAddress.trim())
    : true;

  const cardholderNameValid = cardholderNameSameAsContact
    ? Boolean(form.fullName.trim())
    : true;

  const isOrgProcurementPathway = orgCheckoutPathway === 'procurement';

  const resolvedAccountsPayableEmail = form.accountsPayableEmail.trim() || form.orgEmail.trim();

  const procurementFormComplete = Boolean(
    form.fullName.trim()
    && form.orgName.trim()
    && isInstitutionalEmail(form.orgEmail)
    && form.businessAddress.trim()
    && form.phone.trim()
    && /^[0-9+\-().\s]{8,24}$/.test(form.phone.trim())
    && isInstitutionalEmail(resolvedAccountsPayableEmail),
  );

  const formComplete = isOrgProcurementPathway
    ? procurementFormComplete
    : Boolean(
      form.fullName.trim()
      && form.orgEmail.trim()
      && form.orgName.trim()
      && form.businessAddress.trim()
      && form.phone.trim()
      && cardholderNameValid
      && billingAddressValid,
    );

  const selectedOrgPlan = selectedOrgPlanSeats
    ? SEAT_PLANS.find((plan) => plan.seats === selectedOrgPlanSeats) ?? null
    : null;

  const selectedPlanKey = selectedOrgPlan?.planKey ?? null;

  const hasPricingPlanSelected = selectedOrgPlanSeats !== null;

  const procurementDeclarationsComplete = institutionalAgreed && apBillingAddressAgreed;

  const canSubmit = masterSandboxUnlocked
    ? !processing && hasPricingPlanSelected
    : isOrgProcurementPathway
      ? !processing
      : formComplete && agreed && !processing;

  const canCheckoutSelectedPlan = masterSandboxUnlocked
    ? hasPricingPlanSelected
    : Boolean(selectedOrgPlan?.checkoutEnabled);

  const cardholderNameSameAsContactLabel = 'Credit Card Holder\'s Name is the same as Contact Name';
  const billingSameAsAddressLabel = 'Billing address is the same as Business Address';

  const update = (field: keyof typeof form, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const selectOrgPlan = (seats: SeatTier) => {
    setSelectedOrgPlanSeats(seats);
    setError('');
  };

  const handleCardholderNameSameAsContactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCardholderNameSameAsContact(event.target.checked);
  };

  const handleBillingSameAsAccountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setBillingSameAsBusiness(event.target.checked);
  };

  const handleGenerateInvoice = async () => {
    setError('');

    if (!hasPricingPlanSelected || !selectedPlanKey) {
      setError(PLAN_SELECTION_REQUIRED_MESSAGE);
      return;
    }

    if (!procurementDeclarationsComplete) {
      setError('Accept both mandatory institutional declarations before generating the invoice.');
      return;
    }

    if (!form.fullName.trim()) {
      setError('Enter your full name.');
      return;
    }

    if (!form.orgName.trim()) {
      setError('Enter the organisation name.');
      return;
    }

    if (!isInstitutionalEmail(form.orgEmail)) {
      setError(`Organisation email must use an authorised organisation domain. ${INSTITUTIONAL_EMAIL_HINT}`);
      return;
    }

    if (!form.phone.trim() || !/^[0-9+\-().\s]{8,24}$/.test(form.phone.trim())) {
      setError('Enter a valid phone number (8–24 characters).');
      return;
    }

    if (!form.businessAddress.trim()) {
      setError('Enter the business address.');
      return;
    }

    if (!isInstitutionalEmail(resolvedAccountsPayableEmail)) {
      setError(`Invoice delivery email must use an authorised organisation domain. ${INSTITUTIONAL_EMAIL_HINT}`);
      return;
    }

    setProcessing(true);
    try {
      const seatCount = typeof selectedOrgPlanSeats === 'number' ? selectedOrgPlanSeats : 10;
      const result = await procurementApi.generateInvoice({
        administratorName: form.fullName.trim(),
        organisationName: form.orgName.trim(),
        corporateEmail: form.orgEmail.trim(),
        contactPhone: form.phone.trim(),
        businessAddress: form.businessAddress.trim(),
        poNumber: form.poNumber.trim() || undefined,
        accountsPayableEmail: resolvedAccountsPayableEmail,
        planKey: selectedPlanKey,
        seatCount,
      });

      setAccessKey(result.demoToken);
      setIssuedInvoiceNumber(result.invoiceNumber || '');
      setInvoiceDispatchNotice(result.message || '');
      setProcurementDemoIssued(true);
      sessionStorage.setItem(CLIENT_KEY_STORAGE, result.demoToken);
      sessionStorage.setItem(REGISTRATION_PATHWAY_STORAGE, REGISTRATION_PATHWAY.PROCUREMENT);
    } catch (submitError: unknown) {
      setError((submitError as { error?: string }).error || 'Unable to generate invoice. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isOrgProcurementPathway) {
      await handleGenerateInvoice();
      return;
    }

    if (masterSandboxUnlocked) {
      if (!hasPricingPlanSelected || !selectedPlanKey) {
        setError(PLAN_SELECTION_REQUIRED_MESSAGE);
        return;
      }

      setProcessing(true);
      try {
        const seatCount = typeof selectedOrgPlanSeats === 'number' ? selectedOrgPlanSeats : 10;
        const result = await authApi.registerOrg({
          contact_name: form.fullName.trim() || 'Master Sandbox Admin',
          email: form.orgEmail.trim() || 'master-sandbox@mentology.local',
          org_name: form.orgName.trim() || 'Master Sandbox Organisation',
          address: form.businessAddress.trim() || 'Sandbox Address',
          phone: form.phone.trim() || '0000000000',
          seatCount,
          masterTestingKey: getMasterTestingKey(),
        });
        setAccessKey(result.orgToken);
        await refresh();
      } catch (submitError: unknown) {
        setError((submitError as { error?: string }).error || 'Sandbox registration failed.');
      } finally {
        setProcessing(false);
      }
      return;
    }

    if (!formComplete || !agreed) {
      setError('Complete all fields and accept the mandatory Service Agreement declaration before registering.');
      return;
    }

    if (!hasPricingPlanSelected || !selectedPlanKey) {
      setError(PLAN_SELECTION_REQUIRED_MESSAGE);
      return;
    }

    if (!canCheckoutSelectedPlan) {
      setError('This plan requires negotiated pricing. Please contact Mentology to continue.');
      return;
    }

    setProcessing(true);

    try {
      if (resolvedBillingAddress) {
        sessionStorage.setItem('mentology.orgBillingAddress', resolvedBillingAddress);
      }
      if (resolvedNameOnCard) {
        sessionStorage.setItem('mentology.cardholderName', resolvedNameOnCard);
      }
      sessionStorage.setItem('mentology.selectedPlanKey', selectedPlanKey);

      const checkoutSession = await checkoutApi.createSession({
        planKey: selectedPlanKey,
        customerEmail: form.orgEmail.trim(),
        customerName: form.fullName.trim(),
        companyName: form.orgName.trim(),
        successPath: '/register-org',
        seatCount: typeof selectedOrgPlanSeats === 'number' ? selectedOrgPlanSeats : undefined,
        address: form.businessAddress.trim(),
        phone: form.phone.trim(),
      });

      if (checkoutSession.sandboxBypass) {
        handleGoToExecutivePortal();
        return;
      }

      if (checkoutSession.url) {
        window.location.assign(checkoutSession.url);
        return;
      }

      setError('Unable to start secure checkout. Please try again.');
    } catch (checkoutError: unknown) {
      const checkoutMessage = (checkoutError as { error?: string }).error;
      setError(checkoutMessage || 'Unable to start secure checkout. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!issuedInvoiceNumber) return;
    setInvoiceDownloadError('');
    try {
      await procurementApi.downloadInvoicePdf(issuedInvoiceNumber);
    } catch (downloadError: unknown) {
      setInvoiceDownloadError(
        (downloadError as { error?: string }).error || 'Unable to download invoice PDF. Restart the API server and try again.',
      );
    }
  };

  const handleCopyKey = async () => {
    if (!accessKey) return;
    try {
      await navigator.clipboard.writeText(accessKey);
      setCopyNotice('Copied to clipboard.');
    } catch {
      setCopyNotice('Copy failed — select and copy the key manually.');
    }
  };

  const handleGoToExecutivePortal = () => {
    if (procurementDemoIssued) {
      navigate('/', { replace: true });
      return;
    }
    navigate('/introduction', { replace: true });
  };

  const renderOrgPathwayToggle = () => (
    <div
      className="register-org-pathway-toggle"
      role="radiogroup"
      aria-label="Organisation checkout pathway"
    >
      <button
        type="button"
        role="radio"
        aria-checked={orgCheckoutPathway === 'card'}
        className={`register-org-pathway-option ${orgCheckoutPathway === 'card' ? 'register-org-pathway-option-active' : ''}`}
        onClick={() => { setOrgCheckoutPathway('card'); setError(''); }}
        disabled={processing}
      >
        <span className="register-org-pathway-label">Pathway A</span>
        <span className="register-org-pathway-desc">Credit Card Alignment Parameters</span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={orgCheckoutPathway === 'procurement'}
        className={`register-org-pathway-option ${orgCheckoutPathway === 'procurement' ? 'register-org-pathway-option-active' : ''}`}
        onClick={() => { setOrgCheckoutPathway('procurement'); setError(''); }}
        disabled={processing}
      >
        <span className="register-org-pathway-label">Pathway B</span>
        <span className="register-org-pathway-desc">Corporate Procurement Order</span>
      </button>
    </div>
  );

  const renderProcurementDeclarations = () => (
    <div className="register-org-procurement-declarations">
      <label className="register-org-procurement-checkbox">
        <input
          type="checkbox"
          checked={institutionalAgreed}
          onChange={(e) => setInstitutionalAgreed(e.target.checked)}
          disabled={processing}
        />
        <span>I agree to the mandatory Mentology Institutional Service Agreement.</span>
      </label>
      <label className="register-org-procurement-checkbox">
        <input
          type="checkbox"
          checked={apBillingAddressAgreed}
          onChange={(e) => setApBillingAddressAgreed(e.target.checked)}
          disabled={processing}
        />
        <span>Accounts Payable Billing Address matches the Address provided above.</span>
      </label>
    </div>
  );

  const renderProcurementFields = () => (
    <>
      <input
        className="input-field register-org-input"
        placeholder="Full Name"
        value={form.fullName}
        onChange={(e) => update('fullName', e.target.value)}
        required
      />
      <input
        className="input-field register-org-input"
        type="email"
        placeholder="Organisation Email"
        value={form.orgEmail}
        onChange={(e) => update('orgEmail', e.target.value)}
        required
      />
      <input
        className="input-field register-org-input"
        placeholder="Organisation Name"
        value={form.orgName}
        onChange={(e) => update('orgName', e.target.value)}
        required
      />
      <input
        className="input-field register-org-input"
        placeholder="Business Address"
        value={form.businessAddress}
        onChange={(e) => update('businessAddress', e.target.value)}
        required
      />
      <input
        className="input-field register-org-input"
        placeholder="Phone Number"
        value={form.phone}
        onChange={(e) => update('phone', e.target.value)}
        inputMode="tel"
        required
      />
      <p className="register-org-column-title register-org-procurement-subsection">Procurement Order Details</p>
      <input
        className="input-field register-org-input"
        placeholder="Organisation Purchase Order (PO) Number (optional)"
        value={form.poNumber}
        onChange={(e) => update('poNumber', e.target.value)}
      />
      <input
        className="input-field register-org-input"
        type="email"
        placeholder="Accounts Payable Email (optional — defaults to Organisation Email)"
        value={form.accountsPayableEmail}
        onChange={(e) => update('accountsPayableEmail', e.target.value)}
      />
    </>
  );

  const renderPricingGrid = () => (
    <div className="register-org-plan-section">
      <p className="register-org-column-title">Select Organisation Pack</p>
      <div
        className="register-org-plan-grid"
        role="radiogroup"
        aria-label="Select Organisation Pack"
      >
        {SEAT_PLANS.map((plan) => {
          const isSelected = selectedOrgPlanSeats === plan.seats;
          return (
            <button
              key={String(plan.seats)}
              type="button"
              role="radio"
              aria-checked={isSelected}
              className={`register-org-plan-card ${isSelected ? 'register-org-plan-card-selected' : ''}`}
              onClick={() => selectOrgPlan(plan.seats)}
            >
              <span className="register-org-plan-title">{plan.title}</span>
              <span className="register-org-plan-price">{plan.price}</span>
              <span className="register-org-plan-limit">{plan.limit}</span>
              <span className="register-org-plan-value">{plan.value}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      className="registration-split-screen register-org-screen flex flex-col md:flex-row w-full items-stretch md:items-start"
      data-screen-id={screenId}
      data-screen-surface="org-registration"
      data-screen-route="/register-org"
    >
      <aside
        className="registration-split-brand w-full md:w-[30%] flex flex-col items-center bg-transparent border-none p-0 shadow-none overflow-hidden"
        aria-label="Mentology brand"
      >
        <div className="registration-split-logo-anchor bg-transparent border-none p-0 shadow-none">
          <img
            src={LOGO}
            alt="Mentology"
            className="registration-split-logo bg-transparent border-none p-0 shadow-none"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
      </aside>

      <main className="registration-split-workspace w-full md:w-[70%] flex flex-col p-6 md:p-8 overflow-y-auto max-h-screen pb-40">
      <div className="modal-box register-org-panel">
        <h2 className="section-header register-org-heading">AI-Mentor ELITE</h2>
        <p className="register-org-column-title register-org-heading-sub">Organisation Registration</p>

        <form onSubmit={handleSubmit} className="register-org-form" noValidate>
          {renderOrgPathwayToggle()}

          {isOrgProcurementPathway ? (
            <div className="register-org-procurement-container">
              <div className="register-org-grid">
                <div className="register-org-column register-org-procurement-fields">
                  <p className="register-org-column-title">Registration Details</p>
                  {renderProcurementFields()}
                  {renderProcurementDeclarations()}
                </div>
                <div className="register-org-column">
                  <ServiceAgreementPanel
                    agreed={institutionalAgreed}
                    onAgreedChange={setInstitutionalAgreed}
                    variant="org"
                    hideDeclaration
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="register-org-grid">
              <div className="register-org-column">
                <p className="register-org-column-title">Registration Details</p>
                <input
                  className="input-field register-org-input"
                  placeholder="Full Name"
                  value={form.fullName}
                  onChange={(e) => update('fullName', e.target.value)}
                  required
                />
                <input
                  className="input-field register-org-input"
                  type="email"
                  placeholder="Organisation Email"
                  value={form.orgEmail}
                  onChange={(e) => update('orgEmail', e.target.value)}
                  required
                />
                <input
                  className="input-field register-org-input"
                  placeholder="Organisation Name"
                  value={form.orgName}
                  onChange={(e) => update('orgName', e.target.value)}
                  required
                />
                <input
                  className="input-field register-org-input"
                  placeholder="Business Address"
                  value={form.businessAddress}
                  onChange={(e) => update('businessAddress', e.target.value)}
                  required
                />
                <input
                  className="input-field register-org-input"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                  required
                />
              </div>
              <div className="register-org-column">
                <ServiceAgreementPanel agreed={agreed} onAgreedChange={setAgreed} variant="org" />
              </div>
            </div>
          )}

          {renderPricingGrid()}

          {isOrgProcurementPathway ? null : (
            <div className="register-org-stripe-alignment">
              <p className="register-org-stripe-alignment-label">Credit card alignment parameters</p>
              <div className="register-org-stripe-billing-toggle">
                <input
                  id="register-org-name-same-as-contact"
                  type="checkbox"
                  checked={cardholderNameSameAsContact}
                  onChange={handleCardholderNameSameAsContactChange}
                  disabled={processing}
                />
                <label htmlFor="register-org-name-same-as-contact">
                  {cardholderNameSameAsContactLabel}
                </label>
              </div>
              <div className="register-org-stripe-billing-toggle">
                <input
                  id="register-org-billing-same-as-account"
                  type="checkbox"
                  checked={billingSameAsBusiness}
                  onChange={handleBillingSameAsAccountChange}
                  disabled={processing}
                />
                <label htmlFor="register-org-billing-same-as-account">
                  {billingSameAsAddressLabel}
                </label>
              </div>
            </div>
          )}

          {error && <p className="error-text register-org-error">{error}</p>}

          {isOrgProcurementPathway ? (
            <div className="register-org-secure-checkout">
              <p className="register-org-secure-checkout-copy">
                Generate Invoice compiles a PDF with EFT payment instructions and dispatches it to your Accounts Payable email. A provisional demo access key is issued for Organisation Admin Console setup.
              </p>
              <button
                type="submit"
                className="btn-primary register-org-secure-checkout-btn"
                disabled={!canSubmit}
              >
                {processing ? (
                  <span className="register-org-submit-processing">
                    <span className="register-org-spinner" aria-hidden="true" />
                    Generating invoice...
                  </span>
                ) : (
                  'Generate Invoice'
                )}
              </button>
            </div>
          ) : (
            <div className="register-org-secure-checkout">
              <button
                type="submit"
                className="btn-primary register-org-secure-checkout-btn"
                disabled={!canSubmit}
              >
                {processing ? (
                  <span className="register-org-submit-processing">
                    <span className="register-org-spinner" aria-hidden="true" />
                    Redirecting to secure checkout...
                  </span>
                ) : (
                  'PROCEED TO SECURE CHECKOUT'
                )}
              </button>
            </div>
          )}
        </form>
      </div>
      </main>

      {accessKey && (
        <div
          className="register-org-success-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="org-success-title"
          data-screen-id={screenId}
        >
          <div className={`modal-box copper-border register-org-success-modal ${procurementDemoIssued ? 'register-org-demo-token-modal' : ''}`}>
            <h2 id="org-success-title" className="section-header">
              {procurementDemoIssued ? 'Provisional Demo Access' : 'Registration Successful!'}
            </h2>
            <div className="register-org-success-key-row">
              <p className="register-org-success-subheader scannable-bullet-text">
                {procurementDemoIssued ? (
                  <>
                    Please copy this key to begin setting up your Organisation Admin Console:
                    {' '}
                    <strong className="register-org-demo-token">{accessKey}</strong>
                  </>
                ) : (
                  <>
                    Your Production Client Key: <strong>{accessKey}</strong>
                  </>
                )}
              </p>
              <button
                type="button"
                className="register-org-copy-btn"
                onClick={handleCopyKey}
                title="Copy access key to clipboard"
                aria-label="Copy access key to clipboard"
              >
                ⧉
              </button>
            </div>
            {copyNotice && <p className="register-org-copy-notice scannable-bullet-text">{copyNotice}</p>}
            {procurementDemoIssued && invoiceDispatchNotice && (
              <p className="register-org-copy-notice scannable-bullet-text">{invoiceDispatchNotice}</p>
            )}
            {procurementDemoIssued && issuedInvoiceNumber && (
              <div className="button-control-group">
                <button type="button" className="btn-primary" onClick={handleDownloadInvoice}>
                  Download Invoice PDF
                </button>
              </div>
            )}
            {invoiceDownloadError && (
              <p className="error-text register-org-error">{invoiceDownloadError}</p>
            )}
            <div className="button-control-group">
              <button type="button" className="btn-primary" onClick={handleGoToExecutivePortal}>
                {procurementDemoIssued ? 'Go to Landing Page' : 'Go to Executive Introduction Portal'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
